import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { KeyRound, AlertCircle, Eye, EyeOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ChangePassword() {
  const navigate = useNavigate();
  const { forceChangePassword, mustChangePassword, logout } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Password validation
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Veuillez respecter tous les critères de mot de passe');
      return;
    }

    setLoading(true);

    const result = await forceChangePassword(newPassword);

    if (result.success) {
      toast.success('Mot de passe modifié avec succès!');
      navigate('/dashboard');
    } else {
      setError(result.error);
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Redirect if password change not required
  if (!mustChangePassword) {
    return null;
  }

  const ValidationItem = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-sm ${valid ? 'text-green-600' : 'text-slate-400'}`}>
      {valid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur-sm">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-heading font-bold text-center text-white">
            Changement de mot de passe requis
          </CardTitle>
          <CardDescription className="text-center text-slate-400">
            Pour des raisons de sécurité, veuillez créer un nouveau mot de passe
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2 text-red-300">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="font-body font-medium text-slate-300">
                Nouveau mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-body font-medium text-slate-300">
                Confirmer le mot de passe
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                required
                data-testid="confirm-password-input"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-slate-300 mb-2">Exigences du mot de passe:</p>
              <ValidationItem valid={hasMinLength} text="Au moins 8 caractères" />
              <ValidationItem valid={hasUppercase} text="Au moins une majuscule" />
              <ValidationItem valid={hasLowercase} text="Au moins une minuscule" />
              <ValidationItem valid={hasNumber} text="Au moins un chiffre" />
              <ValidationItem valid={passwordsMatch} text="Les mots de passe correspondent" />
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white h-11 font-medium"
              disabled={loading || !isValid}
              data-testid="change-password-button"
            >
              {loading ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
              onClick={handleLogout}
            >
              Se déconnecter
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
